// frontend/src/pages/Auth.jsx
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiEye,
  FiEyeOff,
  FiMail,
  FiLock,
  FiUser,
  FiPhone,
  FiArrowRight,
  FiArrowLeft,
  FiLogIn,
  FiUserPlus,
  FiCheck,
  FiLoader,
  FiX,
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook } from "react-icons/fa";
import toast from "react-hot-toast";
import "../styles/auth-slider.css";

const OAUTH_ACCOUNTS_KEY = "oauth_accounts";

// Password validation function
const validatePassword = (password) => {
  const errors = [];

  if (password.length < 8) {
    errors.push("8+ chars");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("1 uppercase");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("1 lowercase");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push("1 special");
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength: getPasswordStrength(password),
  };
};

const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

  if (score === 4) return "strong";
  if (score === 3) return "good";
  if (score === 2) return "weak";
  return "very-weak";
};

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const [isActive, setIsActive] = useState(!isLoginPage);
  const hasShownToast = useRef(false);

  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // Register state
  const [userName, setUserName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    errors: [],
    strength: "very-weak",
  });
  const [passwordFocused, setPasswordFocused] = useState(false);

  const [savedAccounts, setSavedAccounts] = useState([]);
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(false);
  const [lastUserEmail, setLastUserEmail] = useState("");
  const { login, register, resendVerification } = useAuth();

  const from = location.state?.from?.pathname || "/dashboard";

  // Validate password on change
  useEffect(() => {
    if (regPassword) {
      setPasswordValidation(validatePassword(regPassword));
    } else {
      setPasswordValidation({
        isValid: false,
        errors: [],
        strength: "very-weak",
      });
    }
  }, [regPassword]);

  // Handle URL parameters for error messages - useLayoutEffect for immediate toast
  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const message = params.get("message");

    if (hasShownToast.current) return;

    if (error === "account_deactivated") {
      hasShownToast.current = true;
      toast.error(
        "Your account has been deactivated. Please contact support.",
        {
          id: "account-deactivated-layout",
          duration: 6000,
        },
      );
      setTimeout(() => {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }, 500);
    } else if (message === "account_deleted") {
      hasShownToast.current = true;
      toast.success("Your account has been successfully deactivated.", {
        id: "account-deleted-layout",
        duration: 5000,
      });
      setTimeout(() => {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }, 500);
    } else if (error === "social_login_failed") {
      hasShownToast.current = true;
      toast.error("Social login failed. Please try again.", {
        id: "social-login-failed-layout",
        duration: 4000,
      });
    } else if (error === "tenant_not_found") {
      hasShownToast.current = true;
      toast.error("Store not found. Please check the URL.", {
        id: "tenant-not-found-layout",
        duration: 4000,
      });
    } else if (error === "tenant_required") {
      hasShownToast.current = true;
      toast.error("Store context required. Please use the correct store URL.", {
        id: "tenant-required-layout",
        duration: 4000,
      });
    } else if (error === "server_error") {
      hasShownToast.current = true;
      toast.error("Server error. Please try again later.", {
        id: "server-error-layout",
        duration: 4000,
      });
    }
  }, []);

  // Update active state when URL changes
  useEffect(() => {
    setIsActive(!isLoginPage);
  }, [isLoginPage]);

  // Load saved OAuth accounts
  useEffect(() => {
    try {
      const stored = localStorage.getItem(OAUTH_ACCOUNTS_KEY);
      if (stored) {
        const accounts = JSON.parse(stored);
        accounts.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
        setSavedAccounts(accounts);
      }
    } catch (error) {
      console.error("Error loading OAuth accounts:", error);
    }
  }, []);

  // Get tenant code from URL - FIXED
  const getTenantCodeFromUrl = () => {
    const hostname = window.location.hostname;
    console.log("getTenantCodeFromUrl - hostname:", hostname);

    // Map for tenant aliases
    const tenantMappings = {
      tooraflexonlinestore: "tooraflex-online",
      tooraflexonline: "tooraflex-online",
      tfonline: "tooraflex-online",
      majestic: "majestic-healthwellness",
      "majestic-health": "majestic-healthwellness",
      lebaba: "lebaba-inware",
      "lebaba-inware": "lebaba-inware",
      terraflex: "terraflex",
    };

    // Check for localhost subdomains
    if (hostname.includes(".localhost") || hostname.includes(".localhost:")) {
      // ✅ FIX: Remove port if present
      const cleanHostname = hostname.split(":")[0];
      const parts = cleanHostname.split(".");
      if (parts.length > 1 && parts[0] !== "www" && parts[0] !== "localhost") {
        const tenant = parts[0];
        const mapped = tenantMappings[tenant] || tenant;
        console.log(`Tenant mapped: ${tenant} -> ${mapped}`);
        return mapped;
      }
    }

    // Check for custom domain (e.g., lebaba-inware.co.za)
    if (hostname !== "localhost" && hostname !== "127.0.0.1") {
      const cleanHostname = hostname.split(":")[0];
      const parts = cleanHostname.split(".");

      // Check for subdomain (tenant.domain.com)
      if (parts.length >= 3) {
        const subdomain = parts[0];
        if (subdomain !== "www" && subdomain !== "tooraflexonlinestore") {
          const mapped = tenantMappings[subdomain] || subdomain;
          console.log(`Tenant from subdomain: ${subdomain} -> ${mapped}`);
          return mapped;
        }
      }

      // Check if it's a custom domain without subdomain
      if (parts.length === 2 || parts.length === 3) {
        const domainName = parts[0];
        if (domainName !== "www" && domainName !== "tooraflexonlinestore") {
          const mapped = tenantMappings[domainName] || domainName;
          console.log(`Tenant from domain: ${domainName} -> ${mapped}`);
          return mapped;
        }
      }
    }

    // Check localStorage
    const storedTenant = localStorage.getItem("tenantCode");
    if (storedTenant && storedTenant !== "null") {
      const mapped = tenantMappings[storedTenant] || storedTenant;
      console.log(`Tenant from storage: ${storedTenant} -> ${mapped}`);
      return mapped;
    }

    // Default fallback
    console.log("No tenant detected, using default: tooraflex-online");
    return "tooraflex-online";
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("savedEmail");
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
    }

    if (
      location.state?.reason === "session-expired" ||
      sessionStorage.getItem("sessionExpired") === "1"
    ) {
      setSessionExpiredNotice(true);
      try {
        const lastUser = JSON.parse(localStorage.getItem("lastUser") || "null");
        if (lastUser?.email && !savedEmail) {
          setEmail(lastUser.email);
        }
        setLastUserEmail(lastUser?.email || "");
      } catch (error) {
        console.error("Error loading last signed-in user:", error);
      }
      sessionStorage.removeItem("sessionExpired");
    }
  }, [location.state]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        if (rememberMe) {
          localStorage.setItem("savedEmail", email);
          localStorage.setItem("rememberMe", "true");
        } else {
          localStorage.removeItem("savedEmail");
          localStorage.removeItem("rememberMe");
        }
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    // Validate password before submission
    if (!passwordValidation.isValid) {
      toast.error("Please meet all password requirements", {
        duration: 4000,
        icon: "🔒",
      });
      return;
    }

    // Validate terms acceptance
    if (!acceptTerms) {
      toast.error("Please accept the Terms and Conditions to continue", {
        duration: 4000,
        icon: "📝",
      });
      return;
    }

    if (regPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setRegLoading(true);

    const tenantCode = getTenantCodeFromUrl();
    console.log("📍 Registration tenant code:", tenantCode);

    if (!tenantCode || tenantCode === "null" || tenantCode === "undefined") {
      toast.error("Invalid store context. Please check the URL.");
      setRegLoading(false);
      return;
    }

    try {
      const result = await register({
        userName: userName,
        email: regEmail,
        phone: phone,
        password: regPassword,
        tenantCode: tenantCode,
        acceptTerms: acceptTerms,
      });

      if (result.success) {
        const userId = result.user?.userId || result.user?.id;
        if (userId && acceptTerms) {
          localStorage.setItem(
            `termsAccepted:${userId}`,
            new Date().toISOString(),
          );
        }
        setEmail(regEmail);
        setPassword("");
        setRegistrationComplete(true);
      }
    } catch (error) {
      console.error("Registration error:", error);
    } finally {
      setRegLoading(false);
    }
  };

  const handleResendSignupVerification = async () => {
    setResendingVerification(true);
    const result = await resendVerification(regEmail, { silent: true });
    if (result.success) {
      toast.success("Verification email resent. Please check your inbox.");
    } else {
      toast.error(result.message || "Failed to resend verification email.");
    }
    setResendingVerification(false);
  };

  const handleSocialLogin = (provider, accountEmail = null) => {
    const tenantCode = getTenantCodeFromUrl();
    const returnUrl = window.location.origin;

    // ✅ Use the correct OAuth endpoint
    let url = `/auth/${provider}?tenantCode=${tenantCode}&returnTo=${returnUrl}`;

    if (accountEmail) {
      url += `&login_hint=${encodeURIComponent(accountEmail)}`;
    }

    console.log(`🔐 Redirecting to ${provider} OAuth:`, url);
    console.log(`📍 Current origin:`, window.location.origin);
    console.log(`📍 Tenant code (mapped):`, tenantCode);

    toast.loading(`Redirecting to ${provider}...`, { id: "social-redirect" });

    window.location.href = url;
  };

  const getProviderIcon = (provider) => {
    if (provider === "google") return <FcGoogle size={20} />;
    if (provider === "facebook")
      return <FaFacebook size={20} color="#1877f2" />;
    return null;
  };

  const switchToLogin = () => {
    setIsActive(false);
    navigate("/login", { state: location.state });
  };

  const switchToRegister = () => {
    setIsActive(true);
    navigate("/register", { state: location.state });
  };

  // Password strength indicator component - Compact
  const PasswordStrengthIndicator = ({ strength }) => {
    const getStrengthColor = () => {
      switch (strength) {
        case "strong":
          return "#4CAF50";
        case "good":
          return "#FFC107";
        case "weak":
          return "#FF9800";
        default:
          return "#f44336";
      }
    };

    const getStrengthLabel = () => {
      switch (strength) {
        case "strong":
          return "Strong";
        case "good":
          return "Good";
        case "weak":
          return "Weak";
        default:
          return "Weak";
      }
    };

    const getStrengthWidth = () => {
      switch (strength) {
        case "strong":
          return "100%";
        case "good":
          return "75%";
        case "weak":
          return "50%";
        default:
          return "25%";
      }
    };

    if (!regPassword) return null;

    return (
      <div className="auth-password-strength">
        <div className="auth-password-strength-bar">
          <div
            className="auth-password-strength-fill"
            style={{
              width: getStrengthWidth(),
              backgroundColor: getStrengthColor(),
              transition: "width 0.3s ease, background-color 0.3s ease",
            }}
          />
        </div>
        <span
          className="auth-password-strength-label"
          style={{ color: getStrengthColor() }}
        >
          {getStrengthLabel()}
        </span>
      </div>
    );
  };

  // Password requirements list component - Compact inline version
  const PasswordRequirements = () => {
    if (!passwordFocused && !regPassword) return null;

    const requirements = [
      { key: "length", label: "8+ chars", test: (v) => v.length >= 8 },
      { key: "uppercase", label: "1 uppercase", test: (v) => /[A-Z]/.test(v) },
      { key: "lowercase", label: "1 lowercase", test: (v) => /[a-z]/.test(v) },
      {
        key: "special",
        label: "1 special",
        test: (v) => /[!@#$%^&*(),.?":{}|<>]/.test(v),
      },
    ];

    return (
      <div className="auth-password-requirements">
        {requirements.map((req) => {
          const isMet = req.test(regPassword);
          return (
            <div key={req.key} className="auth-password-requirement">
              {isMet ? (
                <FiCheck className="auth-req-check" color="#4CAF50" size={12} />
              ) : (
                <FiX className="auth-req-cross" color="#f44336" size={12} />
              )}
              <span
                style={{
                  color: isMet ? "#4CAF50" : "#999",
                }}
              >
                {req.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Terms and Conditions Modal
  const TermsModal = () => (
    <div
      className="auth-terms-modal-overlay"
      onClick={() => setShowTermsModal(false)}
    >
      <div className="auth-terms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-terms-modal-header">
          <h2>Terms and Conditions</h2>
          <button
            className="auth-terms-modal-close"
            onClick={() => setShowTermsModal(false)}
          >
            ×
          </button>
        </div>
        <div className="auth-terms-modal-body">
          <h3>1. Acceptance of Terms</h3>
          <p>
            By creating an account and using our services, you agree to be bound
            by these Terms and Conditions. If you do not agree to these terms,
            please do not use our services.
          </p>

          <h3>2. Account Registration</h3>
          <p>
            You must provide accurate and complete information when creating
            your account. You are responsible for maintaining the
            confidentiality of your account credentials and for all activities
            that occur under your account.
          </p>

          <h3>3. User Obligations</h3>
          <p>
            You agree to use our services in compliance with all applicable laws
            and regulations. You shall not:
          </p>
          <ul>
            <li>Use the services for any illegal or unauthorized purpose</li>
            <li>Share your account credentials with others</li>
            <li>
              Attempt to gain unauthorized access to other users' accounts
            </li>
            <li>Upload or transmit malicious code or viruses</li>
            <li>Harass, abuse, or harm other users</li>
          </ul>

          <h3>4. Privacy Policy</h3>
          <p>
            Your privacy is important to us. We collect and process your
            personal data in accordance with our Privacy Policy. By using our
            services, you consent to such collection and processing.
          </p>

          <h3>5. Intellectual Property</h3>
          <p>
            All content, features, and functionality available through our
            services are the exclusive property of our company and are protected
            by intellectual property laws.
          </p>

          <h3>6. Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at our
            sole discretion, without prior notice, for conduct that we believe
            violates these Terms and Conditions or is harmful to other users or
            our business interests.
          </p>

          <h3>7. Disclaimer of Warranties</h3>
          <p>
            Our services are provided "as is" without warranties of any kind,
            either express or implied. We do not warrant that the services will
            be uninterrupted, error-free, or secure.
          </p>

          <h3>8. Limitation of Liability</h3>
          <p>
            To the fullest extent permitted by law, we shall not be liable for
            any indirect, incidental, special, consequential, or punitive
            damages arising from your use of our services.
          </p>

          <h3>9. Changes to Terms</h3>
          <p>
            We reserve the right to update or modify these Terms and Conditions
            at any time. We will notify you of any material changes via email or
            through our services.
          </p>

          <h3>10. Contact Information</h3>
          <p>
            If you have any questions about these Terms and Conditions, please
            contact us at:
            <br />
            <strong>Email:</strong> info@tooraflex.co.za
            <br />
          </p>

          <div className="auth-terms-modal-footer">
            <button
              className="auth-terms-modal-btn-primary"
              onClick={() => {
                setAcceptTerms(true);
                setShowTermsModal(false);
                toast.success("Terms accepted!", { duration: 2000 });
              }}
            >
              Accept Terms
            </button>
            <button
              className="auth-terms-modal-btn-secondary"
              onClick={() => {
                setShowTermsModal(false);
                toast.info("Please review the terms to continue", {
                  duration: 3000,
                });
              }}
            >
              Review Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (registrationComplete) {
    return (
      <div className="auth-page">
        <section
          className="auth-email-confirmation"
          aria-labelledby="registration-confirmation-title"
        >
          <FiMail className="auth-email-confirmation-icon" aria-hidden="true" />
          <h1 id="registration-confirmation-title">Check your inbox</h1>
          <p>
            We sent a verification email to <strong>{regEmail}</strong>. Open
            the message and click the verification button to finish creating
            your account. You will be signed in automatically.
          </p>
          <button
            type="button"
            className="auth-submit-btn"
            onClick={handleResendSignupVerification}
            disabled={resendingVerification}
          >
            {resendingVerification ? "Sending..." : "Resend verification email"}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className={`auth-slider-container ${isActive ? "active" : ""}`}>
        {/* Terms Modal */}
        {showTermsModal && <TermsModal />}

        {/* Mobile Toggle Buttons */}
        <div className="auth-mobile-toggle">
          <button
            className={`auth-mobile-toggle-btn ${!isActive ? "active" : ""}`}
            onClick={switchToLogin}
            type="button"
          >
            Sign In
          </button>
          <button
            className={`auth-mobile-toggle-btn ${isActive ? "active" : ""}`}
            onClick={switchToRegister}
            type="button"
          >
            Sign Up
          </button>
        </div>

        {/* Sign In Form */}
        <div className="auth-form-container auth-sign-in">
          <form className="auth-form" onSubmit={handleLoginSubmit}>
            <h1>Sign In</h1>
            {sessionExpiredNotice && (
              <div role="alert" className="auth-session-expired-notice">
                Session expired. Sign in again
                {lastUserEmail && <> to continue as {lastUserEmail}</>}.
              </div>
            )}

            {/* Saved OAuth Accounts */}
            {savedAccounts.length > 0 && (
              <div className="auth-oauth-saved-list">
                {savedAccounts.slice(0, 2).map((account) => (
                  <button
                    key={account.email}
                    type="button"
                    className="auth-oauth-saved-btn"
                    onClick={() =>
                      handleSocialLogin(account.provider, account.email)
                    }
                    disabled={loginLoading}
                  >
                    <div className="auth-oauth-saved-avatar">
                      <div className="auth-oauth-avatar-placeholder">
                        {account.name?.charAt(0) || account.email.charAt(0)}
                      </div>
                    </div>
                    <div className="auth-oauth-saved-info">
                      <div className="auth-oauth-saved-name">
                        {account.name || account.email.split("@")[0]}
                      </div>
                      <div className="auth-oauth-saved-email">
                        {account.email}
                      </div>
                    </div>
                    {getProviderIcon(account.provider)}
                  </button>
                ))}
              </div>
            )}

            <div className="auth-social-icons">
              <button
                type="button"
                className="auth-icon"
                onClick={() => handleSocialLogin("google")}
              >
                <FcGoogle size={20} />
              </button>
              <button
                type="button"
                className="auth-icon"
                onClick={() => handleSocialLogin("facebook")}
              >
                <FaFacebook size={20} color="#1877f2" />
              </button>
            </div>
            <span>or use your email password</span>

            <div className="auth-input-wrapper">
              <FiMail className="auth-input-icon" />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loginLoading}
              />
            </div>

            <div className="auth-input-wrapper">
              <FiLock className="auth-input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loginLoading}
              />
              <button
                type="button"
                className="auth-password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>

            <div className="auth-options">
              <label className="auth-checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <Link to="/forgot-password">Forgot Password?</Link>
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loginLoading}
            >
              {loginLoading ? (
                <>
                  <FiLoader className="auth-spinner" /> Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>

        {/* Sign Up Form */}
        <div className="auth-form-container auth-sign-up">
          <form className="auth-form" onSubmit={handleRegisterSubmit}>
            <h1>Create Account</h1>
            <div className="auth-social-icons">
              <button
                type="button"
                className="auth-icon"
                onClick={() => handleSocialLogin("google")}
              >
                <FcGoogle size={20} />
              </button>
              <button
                type="button"
                className="auth-icon"
                onClick={() => handleSocialLogin("facebook")}
              >
                <FaFacebook size={20} color="#1877f2" />
              </button>
            </div>
            <span>or use your email for registration</span>

            <div className="auth-input-wrapper">
              <FiUser className="auth-input-icon" />
              <input
                type="text"
                placeholder="Full Name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                disabled={regLoading}
              />
            </div>

            <div className="auth-input-wrapper">
              <FiMail className="auth-input-icon" />
              <input
                type="email"
                placeholder="Email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                required
                disabled={regLoading}
              />
            </div>

            <div className="auth-input-wrapper">
              <FiPhone className="auth-input-icon" />
              <input
                type="tel"
                placeholder="Phone (Optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={regLoading}
                autoComplete="tel"
              />
            </div>

            {/* Password field with validation */}
            <div className="auth-input-wrapper auth-password-wrapper">
              <FiLock className="auth-input-icon" />
              <input
                type={showRegPassword ? "text" : "password"}
                placeholder="Password (8+ chars, 1 uppercase, 1 lowercase, 1 special)"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                required
                disabled={regLoading}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                className={
                  regPassword && !passwordValidation.isValid
                    ? "auth-input-error"
                    : ""
                }
              />
              <button
                type="button"
                className="auth-password-toggle-btn"
                onClick={() => setShowRegPassword(!showRegPassword)}
              >
                {showRegPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>

            {/* Password strength indicator */}
            <PasswordStrengthIndicator strength={passwordValidation.strength} />

            {/* Password requirements */}
            <PasswordRequirements />

            {/* Confirm Password field */}
            <div className="auth-input-wrapper auth-password-wrapper">
              <FiLock className="auth-input-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={regLoading}
                className={
                  confirmPassword && regPassword !== confirmPassword
                    ? "auth-input-error"
                    : confirmPassword && regPassword === confirmPassword
                      ? "auth-input-success"
                      : ""
                }
              />
              <button
                type="button"
                className="auth-password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <FiEyeOff size={16} />
                ) : (
                  <FiEye size={16} />
                )}
              </button>
            </div>

            {/* Confirm password match indicator */}
            {confirmPassword && (
              <div className="auth-password-match">
                {regPassword === confirmPassword ? (
                  <span style={{ color: "#4CAF50" }}>
                    <FiCheck size={12} /> Passwords match
                  </span>
                ) : (
                  <span style={{ color: "#f44336" }}>
                    <FiX size={12} /> Passwords do not match
                  </span>
                )}
              </div>
            )}

            {/* Terms and Conditions Checkbox */}
            <div className="auth-terms-wrapper">
              <label className="auth-terms-checkbox">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={regLoading}
                />
                <span className="auth-terms-checkmark">
                  {acceptTerms && <FiCheck size={14} />}
                </span>
                <span className="auth-terms-text">
                  I agree to the{" "}
                  <button
                    type="button"
                    className="auth-terms-link"
                    onClick={() => setShowTermsModal(true)}
                  >
                    Terms and Conditions
                  </button>{" "}
                  and{" "}
                  <button
                    type="button"
                    className="auth-terms-link"
                    onClick={() => setShowTermsModal(true)}
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={regLoading || !passwordValidation.isValid}
            >
              {regLoading ? (
                <>
                  <FiLoader className="auth-spinner" /> Creating Account...
                </>
              ) : (
                "Sign Up"
              )}
            </button>
          </form>
        </div>

        {/* Toggle Container with Animated Buttons */}
        <div className="auth-toggle-container">
          <div className="auth-toggle">
            <div className="auth-toggle-panel auth-toggle-left">
              <div className="auth-toggle-content">
                <FiLogIn className="auth-toggle-icon" />
                <h1>Welcome Back!</h1>
                <p>Enter your personal details to use all of site features</p>
                <button className="auth-toggle-btn" onClick={switchToLogin}>
                  Sign In <FiArrowRight className="auth-btn-icon" />
                </button>
              </div>
            </div>
            <div className="auth-toggle-panel auth-toggle-right">
              <div className="auth-toggle-content">
                <FiUserPlus className="auth-toggle-icon" />
                <h1>Hello, Friend!</h1>
                <p>
                  Register with your personal details to use all of site
                  features
                </p>
                <button className="auth-toggle-btn" onClick={switchToRegister}>
                  <FiArrowLeft className="auth-btn-icon" /> Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
